import * as React from 'react';
import { ContentList } from '~/Blocks/ContentListBlock/ContentList';
import { CollapseList } from '~/Partials/CollapseList/CollapseList';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import { LinkListSection } from '~/Partials/LinkListSection/LinkListSection';
import { LinkListSectionColumnsLayout } from '~/Partials/LinkListSection/LinkListSectionColumnsLayout.csharp';
import { LinkListSectionType } from '~/Partials/LinkListSection/LinkListSectionType.csharp';
import { LinkListSectionVariant } from '~/Partials/LinkListSection/LinkListSectionVariant.csharp';
import { LinkWithDescriptionListSection } from '~/Partials/LinkWithDescriptionListSection/LinkWithDescriptionListSection';
import { NavigationBlock } from '~/Partials/NavigationBlock/NavigationBlock';
import { SectionWithAnchor } from '~/Partials/NavigationBlock/SectionWithAnchor';
import { PersonInfoList } from '~/Partials/PersonInfoList/PersonInfoList';
import { ResearchPublication } from '~/Partials/ResearchPublication/ResearchPublication';
import type { ResearchProjectContentViewModel } from './ViewModel/ResearchProjectContentViewModel.csharp';

export const ResearchProjectContent: React.FC<React.PropsWithChildren<ResearchProjectContentViewModel>> = props => {
    const {
        name,
        translations,
        description,
        projectPeriod,
        categories,
        infoItems,
        navigationBlockProps,
        participantsSection,
        publicationsSection,
        relatedProjects,
        usefulLinks,
        contentListSection,
    } = props;

    return (
        <section className="ResearchProject__content">
            <div className="ResearchProject__baseInfo">
                <h1 className="ResearchProject__title">{name}</h1>
                <div className="Layout__titleTopSectionsBelow">
                    <div className="Layout__info">
                        {description && <p className="ResearchProject__description">{description}</p>}
                        <ul className="ResearchProject__detailedInfo">
                            {projectPeriod && (
                                <li>
                                    <strong>{`${translations.projectPeriod}: `}</strong>
                                    <span>{projectPeriod}</span>
                                </li>
                            )}
                            {categories.length > 0 && (
                                <li>
                                    <strong>{`${translations.categoryLabel}: `}</strong>
                                    <span>{categories.join(', ')}</span>
                                </li>
                            )}
                        </ul>
                    </div>
                    <div className="Layout__aside">
                        <div className="ResearchProject__navigationBlockWrapper">
                            <NavigationBlock {...navigationBlockProps} />
                        </div>
                    </div>
                    <div className="ResearchProject__richtext Layout__content">
                        {infoItems.length > 0 && (
                            <div className="RichtextContent">
                                {infoItems.map(paragraph => (
                                    <div key={paragraph.heading}>
                                        <h2 className="ResearchProject__infoItemHeading">{paragraph.heading}</h2>
                                        {paragraph.body && <HtmlDiv html={paragraph.body} />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="ResearchProject__sections">
                <SectionWithAnchor
                    section={participantsSection}
                    renderContent={sectionProps => (
                        <PersonInfoList title={participantsSection.title} persons={sectionProps} />
                    )}
                />
                <SectionWithAnchor
                    section={publicationsSection}
                    renderContent={sectionProps => (
                        <CollapseList
                            title={publicationsSection.title}
                            initialLength={3}
                            translations={translations.collapseList}
                            items={sectionProps}
                            renderItem={item => <ResearchPublication {...item} />}
                        />
                    )}
                />

                <SectionWithAnchor
                    section={contentListSection}
                    renderContent={sectionProps => <ContentList {...sectionProps} />}
                />
                {relatedProjects.length > 0 && (
                    <LinkWithDescriptionListSection title={translations.relatedProjects} items={relatedProjects} />
                )}
                {usefulLinks.length > 0 && (
                    <LinkListSection
                        type={LinkListSectionType.Plain}
                        title={translations.usefulLinks}
                        items={usefulLinks.map(link => ({ link }))}
                        variant={LinkListSectionVariant.Grey}
                        columnsLayout={LinkListSectionColumnsLayout.Single}
                    />
                )}
            </div>
        </section>
    );
};
